import type { ReservationReportRow } from '../../api/types/reports';

/** The API's post-cancellation status vocabulary on `ReservationReportRow.statu`
 *  ("Cancelled" / "Void" / "Refund"), matched loosely so Turkish variants
 *  ("İptal", "İade Edildi") are caught too. */
const CANCELLED_STATUS_RE = /iptal|iade|cancel|void|refund/;

export function isCancelledStatus(statu?: string | null): boolean {
  if (!statu) return false;
  // Fold the Turkish dotted "İ" to ASCII before lowercasing — a plain `/iptal/i`
  // does NOT match "İptal" (the `i` flag can't case-fold U+0130).
  const normalized = statu.replace(/İ/g, 'I').toLowerCase();
  return CANCELLED_STATUS_RE.test(normalized);
}

export interface CancellationSummary {
  /** `full` — every ticket/pax row is cancelled; `partial` — only some. */
  scope: 'full' | 'partial';
  /** Distinct status labels seen on the cancelled rows (e.g. `['Void', 'Refund']`). */
  statuses: string[];
}

/** Summarises cancellation across every ticket/pax row of a reservation so the detail
 *  page can flag it up front. Returns `null` when nothing is cancelled. */
export function cancellationSummary(rows: ReservationReportRow[]): CancellationSummary | null {
  if (rows.length === 0) return null;
  const cancelled = rows.filter((r) => isCancelledStatus(r.statu));
  if (cancelled.length === 0) return null;
  const statuses = Array.from(
    new Set(cancelled.map((r) => (r.statu ?? '').trim()).filter(Boolean))
  );
  return { scope: cancelled.length === rows.length ? 'full' : 'partial', statuses };
}
